<template>
  <div class="flex h-full flex-col">
    <div class="posts-header flex items-center justify-between px-3 py-3">
      <div class="breadcrumbs flex-1">
        <button
          class="tb-btn-link high-em text-[13px] leading-none"
          data-entity="producer-assets-backToTagboards"
          data-test="producer-assets-curatedContent-breadCrumb"
          @click="$emit('back')"
        >
          Boards
        </button>

        <span class="slash mx-1">
          /
        </span>

        <span class="tagboard-title" :title="tagboard.title" :data-test="`producer-assets-curatedContent-currentBoard:${tagboard._id}`">
          {{ tagboard.title }}
        </span>
      </div>
    </div>

    <div ref="sourceList" class="posts-wrapper flex-1 overflow-auto px-3 pt-0">
      <draggable
        v-if="posts"
        class="posts clearfix mt-0"
        v-model="posts"
        :clone="clone"
        v-bind="{sort:false, animation: 200, group: { name: 'posts', pull:'clone', put: false }}"
        data-test="producer-assets-curatedContent-list"
        @start="onStart"
        :move="checkMove"
        revert-on-spill="true"
        @unchoose="onUnchoose"
      >
        <post
          v-for="(post, index) in posts"
          :key="index"
          data-entity="producer-assets-tagboardPost"
          :data-test="`producer-assets-content-${post.post_id}`"
          :post="post"
        />
      </draggable>

      <div ref="loadTrigger" class="load-trigger flex min-h-16 w-full items-center justify-center">
        <div v-if="isLoading" class="loading loading--inline m-0" data-test="producer-assets-curatedContent-loading">
          <i class="fas fa-spinner fa-spin"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { pick } from 'lodash-es';

import tabMixins from '../../mixins/tab-mixins';
import PostComponent from '../../components/post/Post.vue';

const { v1: uuidv1 } = require('uuid');

export default {
  mixins: [tabMixins],
  components: {
    post: PostComponent,
  },
  props: {
    tagboardId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      count: 50,
      hasMorePosts: true,
      before: null,
      dropzones: null,
      error: false,
      loading: false,
    };
  },

  computed: {
    tagboard() {
      return this.$store.state.tagboards.lookupTable[this.tagboardId];
    },

    posts() {
      return this.$store.state.posts.byTagboard[this.tagboardId];
    },

    isLoading() {
      return this.$store.state.posts.isLoading[this.tagboardId] || false;
    },
  },

  created() {
    // Clear existing data so everything is fresh
    // We could just leave it alone in the future and make the user use the refresh button,
    // but some users probably expect it to reload when going back and forth
    this.$store.dispatch('clearTagboardPosts', this.tagboardId);
    this.fetchData();
  },

  mounted() {
    if (!this.tagboard) {
      this.$store.dispatch('getTagboard', { tagboardId: this.tagboardId });
    }

    if (this.$refs.sourceList) {
      this.intersectionObserver = new IntersectionObserver(this.fetchData, {
        root: this.$refs.sourceList,
      });

      this.intersectionObserver.observe(this.$refs.loadTrigger);
    }
  },

  methods: {
    clone(original) {
      const clone = pick(original, ['post_id', 'network', 'permalink']);
      clone.timeline_id = uuidv1();
      clone.from_tagboard = this.tagboardId;

      this.$store.dispatch('addToPostsLookupTable', original);
      return clone;
    },

    onStart() {
      const dropzones = document.getElementsByClassName('tgbPostDrop');
      for (let i = 0; i < dropzones.length; i += 1) {
        dropzones[i].classList.add('tgbDropArea--active');
      }
    },

    checkMove(e) {
      const { type, post_id: postId } = e.draggedContext.element;

      // Prevent duplicates in post list
      const isInList = e.relatedContext.list.some((i) => i?.post_id === postId);
      const hasContent = Boolean(e.relatedContext.list.length);
      const hasTagboard = e.relatedContext.list.some((i) => i.type === 'tagboard');
      const tagboardInvolved = (hasContent && type === 'tagboard') || hasTagboard;
      const notAllowed = tagboardInvolved || isInList;
      const errorMessages = {
        tagboard: 'A full Board cannot be presented with other content.',
        duplicate: 'Duplicate posts not allowed.',
      };

      if (notAllowed) {
        e.to.classList.add('tgbDropArea--no');
        e.dragged.style.cursor = 'not-allowed';
        this.error = errorMessages[tagboardInvolved ? 'tagboard' : 'duplicate'];
        return false;
      }

      this.error = false;
      return true;
    },

    onUnchoose(e) {
      e.item.style.cursor = 'pointer';
      if (this.error) {
        this.errorToast(this.error);
      }

      document.querySelectorAll('.tgbDropArea--no').forEach((el) => {
        el.classList.remove('tgbDropArea--no');
        this.error = false;
      });

      document.querySelectorAll('.tgbDropArea--active').forEach((el) => {
        el.classList.remove('tgbDropArea--active');
      });
    },

    refresh() {
      this.before = null;
      this.fetchData();
    },

    async fetchData() {
      if (!this.hasMorePosts || this.loading) { return; }

      this.loading = true;

      const data = await this.$store.dispatch('getPostsByTagboard', {
        tagboardId: this.tagboardId,
        count: this.count,
        before: this.before,
      });

      // No data == failed to load posts by tagboard
      if (!data) {
        this.loading = false;
        return;
      }

      this.before = data.meta.before;

      if (!data.meta.count) {
        this.hasMorePosts = false;
      }

      this.loading = false;
    },
  },
};
</script>
